// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Counts pages in the browser before upload so a document over BDA's limit is rejected with the same message the
// API would return, without spending the upload (requirement 5.2; design 4.4). The API counts again on start.
import { useEffect, useState } from 'react';
import { MAX_DOCUMENT_PAGES, tooManyPagesMessage } from '@bda/shared';
import { pdfjs } from 'react-pdf';
import * as UTIF from 'utif';
import { contentTypeOf, validateFile } from './upload';
import { pdfOptions } from './pdfjsWorker';

/** Pages in a PDF, TIFF, PNG, or JPEG; null when the browser cannot read the file (the server decides then). */
export async function countFilePages(file: File): Promise<number | null> {
  const type = contentTypeOf(file);
  if (type === 'image/png' || type === 'image/jpeg') return 1;
  try {
    if (type === 'image/tiff') return UTIF.decode(await file.arrayBuffer()).length || null;
    if (type === 'application/pdf') {
      const task = pdfjs.getDocument({ ...pdfOptions, data: new Uint8Array(await file.arrayBuffer()) });
      try {
        return (await task.promise).numPages;
      } finally {
        void task.destroy();
      }
    }
  } catch {
    return null;
  }
  return null;
}

/** Error text for a file over the page limit, else null. */
export async function pageLimitError(file: File): Promise<string | null> {
  const pages = await countFilePages(file);
  return pages !== null && pages > MAX_DOCUMENT_PAGES ? tooManyPagesMessage(pages) : null;
}

/** A tighter page limit than BDA's, with the message the API returns for it (a Sample_Packet, blueprint-sets Req 9.2). */
export interface PageLimit {
  maxPages: number;
  message: (pages: number, maxPages: number) => string;
}

export interface FileCheck {
  error: string | null;
  /** True while pages are being counted. */
  checking: boolean;
  /** The counted pages; null when there is no file, it failed the type/size checks, is being counted, or is unreadable. */
  pages: number | null;
}

/**
 * Type and size checks at once, then the page count against BDA's limit and the optional tighter `limit`;
 * `checking` is true while pages are being counted.
 */
export function useFileCheck(file: File | undefined, limit?: PageLimit): FileCheck {
  const basic = file ? validateFile(file) : null;
  const [counted, setCounted] = useState<{ file: File; pages: number | null } | null>(null);
  useEffect(() => {
    if (!file || basic) return;
    let live = true;
    void countFilePages(file).then((pages) => live && setCounted({ file, pages }));
    return () => {
      live = false;
    };
  }, [file, basic]);
  if (!file) return { error: null, checking: false, pages: null };
  if (basic) return { error: basic, checking: false, pages: null };
  if (counted?.file !== file) return { error: null, checking: true, pages: null };
  const { pages } = counted;
  let error: string | null = null;
  if (pages !== null && pages > MAX_DOCUMENT_PAGES) error = tooManyPagesMessage(pages);
  else if (pages !== null && limit && pages > limit.maxPages) error = limit.message(pages, limit.maxPages);
  return { error, checking: false, pages };
}
